import { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateRangeFilter } from './date-range-filter';
import { MultiFacetFilter, SingleFacetFilter, type FacetOption } from './facet-filter';
import { SortControl } from './sort-control';
import { ViewToggle, type CatalogueView } from './view-toggle';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { MIN_QUERY_LENGTH, type CatalogueFilters } from '@/lib/filters';
import { cn } from '@/lib/utils';

function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [text, setText] = useState(value);
  const debounced = useDebouncedValue(text, 350);
  const lastSynced = useRef(value);

  // Push the debounced text up into the URL.
  useEffect(() => {
    if (debounced === lastSynced.current) return;
    lastSynced.current = debounced;
    onChange(debounced);
  }, [debounced, onChange]);

  // Pull external changes (a cleared chip, a shared URL) back into the box.
  useEffect(() => {
    if (value === lastSynced.current) return;
    lastSynced.current = value;
    setText(value);
  }, [value]);

  const tooShort = text.trim().length > 0 && text.trim().length < MIN_QUERY_LENGTH;

  return (
    <div className="relative flex-1">
      <Label htmlFor="catalogue-search" className="sr-only">
        Search links
      </Label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        id="catalogue-search"
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Search titles, descriptions and URLs…"
        className="h-9 pl-9 pr-9"
        aria-describedby={tooShort ? 'catalogue-search-hint' : undefined}
      />
      {text.length > 0 ? (
        <button
          type="button"
          onClick={() => setText('')}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground',
            'transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring',
          )}
        >
          <X aria-hidden="true" className="h-3.5 w-3.5" />
          <span className="sr-only">Clear search</span>
        </button>
      ) : null}
      <p
        id="catalogue-search-hint"
        role="status"
        className={cn('mt-1 text-xs text-muted-foreground', !tooShort && 'sr-only')}
      >
        {tooShort ? `Type at least ${MIN_QUERY_LENGTH} characters to search.` : ''}
      </p>
    </div>
  );
}

export interface CatalogueToolbarProps {
  filters: CatalogueFilters;
  patchFilters: (patch: Partial<CatalogueFilters>) => void;
  view: CatalogueView;
  onViewChange: (view: CatalogueView) => void;
  categoryOptions: FacetOption[];
  categoriesLoading: boolean;
  sharerOptions: FacetOption[];
  sharersLoading: boolean;
  channelOptions: FacetOption[];
  channelsLoading: boolean;
  domainOptions: FacetOption[];
  domainsLoading: boolean;
  isAdmin: boolean;
}

export function CatalogueToolbar({
  filters,
  patchFilters,
  view,
  onViewChange,
  categoryOptions,
  categoriesLoading,
  sharerOptions,
  sharersLoading,
  channelOptions,
  channelsLoading,
  domainOptions,
  domainsLoading,
  isAdmin,
}: CatalogueToolbarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const onQueryChange = useCallback((query: string) => patchFilters({ query }), [patchFilters]);

  return (
    <section aria-label="Search and filters" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start gap-2">
        <SearchField value={filters.query} onChange={onQueryChange} />

        <Button
          type="button"
          variant="outline"
          className="h-9 lg:hidden"
          aria-expanded={filtersOpen}
          aria-controls="catalogue-filters"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
          Filters
        </Button>

        <div className="hidden items-center gap-2 lg:flex">
          <SortControl
            sort={filters.sort}
            order={filters.order}
            onChange={({ sort, order }) => patchFilters({ sort, order })}
          />
          <ViewToggle view={view} onChange={onViewChange} />
        </div>
      </div>

      <div
        id="catalogue-filters"
        className={cn('flex-col gap-3', filtersOpen ? 'flex' : 'hidden lg:flex')}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <MultiFacetFilter
            label="Categories"
            options={categoryOptions}
            loading={categoriesLoading}
            selected={filters.categories}
            emptyMessage="No categories have been created yet."
            onChange={(categories) => patchFilters({ categories })}
          />
          <SingleFacetFilter
            label="First shared by"
            options={sharerOptions}
            loading={sharersLoading}
            selected={filters.sharerId}
            emptyMessage="No sharers recorded yet."
            onChange={(sharerId) => patchFilters({ sharerId })}
          />
          <SingleFacetFilter
            label="First seen in"
            options={channelOptions}
            loading={channelsLoading}
            selected={filters.channelId}
            emptyMessage="No channels recorded yet."
            onChange={(channelId) => patchFilters({ channelId })}
          />
          <SingleFacetFilter
            label="Domain"
            options={domainOptions}
            loading={domainsLoading}
            selected={filters.domain}
            emptyMessage="No domains recorded yet."
            onChange={(domain) => patchFilters({ domain })}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangeFilter
            from={filters.from}
            to={filters.to}
            onChange={({ from, to }) => patchFilters({ from, to })}
            className="w-full sm:w-72"
          />

          {isAdmin ? (
            <div className="flex items-center gap-2">
              <Checkbox
                id="filter-archived"
                checked={filters.archived}
                onCheckedChange={(checked) => patchFilters({ archived: checked === true })}
              />
              <Label htmlFor="filter-archived" className="flex cursor-pointer items-center gap-1.5">
                <Archive aria-hidden="true" className="h-3.5 w-3.5" />
                Show archived links
              </Label>
            </div>
          ) : null}

          <div className="flex items-center gap-2 lg:hidden">
            <SortControl
              sort={filters.sort}
              order={filters.order}
              onChange={({ sort, order }) => patchFilters({ sort, order })}
            />
            <ViewToggle view={view} onChange={onViewChange} />
          </div>
        </div>
      </div>
    </section>
  );
}
